import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import clsx from 'clsx';

const tones = {
  primary: 'bg-primary-50 text-primary-700',
  navy: 'bg-navy-50 text-navy-700',
  success: 'bg-success-50 text-success-700',
  warning: 'bg-warning-50 text-warning-700',
  danger: 'bg-danger-50 text-danger-700',
  gray: 'bg-gray-100 text-gray-600',
} as const;

interface KpiCardProps {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  icon: LucideIcon;
  tone?: keyof typeof tones;
  to?: string;
  title?: string;
}

/** Dashboard KPI tile: label, large value, optional hint, tinted icon. */
export function KpiCard({ label, value, hint, icon: Icon, tone = 'primary', to, title }: KpiCardProps) {
  const body = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-500">{label}</p>
        <p className="mt-2 text-2xl font-semibold tracking-tight text-gray-900 tabular-nums">{value}</p>
        {hint && <p className="mt-1 truncate text-xs text-gray-500">{hint}</p>}
      </div>
      <div className={clsx('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', tones[tone])}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  );

  const className = 'card p-5 block';
  return to ? (
    <Link to={to} title={title} className={clsx(className, 'card-interactive')}>
      {body}
    </Link>
  ) : (
    <div title={title} className={className}>
      {body}
    </div>
  );
}
